import { Space, type SpaceProps, Switch, type SwitchProps } from 'antd';
import { omit } from 'lodash';
import type React from 'react';
import { useInputController } from '../hooks/useInputController.ts';
import { IsBooleanCheckboxElementSymbol } from '../item/symbols/isBooleanCheckboxElement.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

export interface ISwitchInputProps extends Omit<SwitchProps, 'name'> {
  name: string;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   * solo per compatibilita per il passaggio della proprietà
   * ma non è gestito il comportamento
   */
  readOnly?: boolean;

  children?: React.ReactNode;
  onChange?: (checked: boolean) => void;

  /**
   * Inverte la rappresentazione visiva dello switch rispetto al valore reale del campo.
   *
   * Utile quando la semantica del campo è negativa (es. "disabilita X")
   * ma si vuole presentare all'utente un'azione positiva (es. "abilita X").
   *
   * - Switch ON  → valore `false`
   * - Switch OFF → valore `true`
   */
  showInverted?: boolean;
  /**
   *  Allinea verticalmente lo switch con il testo dei children.
   *  Utile quando i children sono multilinea o di altezza maggiore dello switch.
   *  Di default, lo switch è allineato al centro verticalmente rispetto ai children.
   */
  align?: SpaceProps['align'];
}

export function SwitchInput(props: ISwitchInputProps) {
  const { name, children, onChange, showInverted, align, ...inputProps } =
    props;

  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const { field } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();

  // Quando showInverted è true, invertiamo il valore per la visualizzazione e per l'onChange
  // In questo modo, il valore reale del campo è sempre coerente (es. `true` significa "disabilitato")
  const displayValue = showInverted ? !field.value : field.value;

  const switchNode = (
    <Switch
      disabled={visuallyDisabled}
      {...inputProps}
      {...field}
      onChange={checked => {
        const actualValue = showInverted ? !checked : checked;
        field.onChange(actualValue);
        if (onChange) {
          onChange(actualValue);
        }
      }}
      // Gli Switch non supportano aria-required, in quanto non esiste un valore "indeterminato"
      {...omit(accessibility.attributes, ['aria-required'])}
      checked={displayValue}
      aria-checked={displayValue}
      // checkedChildren={inputProps.checkedChildren ?? 'Sì'}
      // unCheckedChildren={inputProps.unCheckedChildren ?? 'No'}
    />
  );

  return children ? (
    <Space align={align}>
      {switchNode}
      {children}
    </Space>
  ) : (
    switchNode
  );
}

SwitchInput[IsBooleanCheckboxElementSymbol] = true;
